import React, { useMemo } from 'react';
import { Button, ListGroup, Spinner } from 'react-bootstrap';
import api from '../../services/api';

function formatSupportDate(dateString, language, t) {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t('support.justNow');
  if (minutes < 60) return t('support.minutesAgo', { minutes });
  const localeMap = { en: 'en-US', ru: 'ru-RU', zh: 'zh-CN', tr: 'tr-TR', ar: 'ar-SA' };
  const locale = localeMap[language] || 'en-US';
  if (diff < 86400000) {
    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  }
  return `${date.toLocaleDateString(locale)} ${date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`;
}

function SupportMessageList({
  messages,
  userId,
  isAdmin,
  embeddedInChatApp,
  language,
  t,
  highlightedMessageId,
  translatingMessageId,
  markingUnreadId,
  setMessageRef,
  onReply,
  onJumpToReply,
  onTranslateMessage,
  onMarkUnread,
  messagesEndRef,
}) {
  const formattedDates = useMemo(() => {
    const map = new Map();
    for (const message of messages) {
      map.set(message.id, formatSupportDate(message.created_at, language, t));
    }
    return map;
  }, [messages, language, t]);

  return (
    <ListGroup variant="flush" className={embeddedInChatApp ? 'chat-app-support-messages' : ''}>
      {messages.map((message) => {
        const isOwnMessage = message.sender_id === userId;
        const isSystem = message.message_type === 'system';
        const createdLabel = formattedDates.get(message.id) || '';

        if (embeddedInChatApp) {
          if (isSystem) {
            return (
              <div key={message.id} className="chat-support-system-line">
                {message.content}
              </div>
            );
          }
          return (
            <div
              key={message.id}
              className={`chat-bubble-row ${isOwnMessage ? 'user' : 'bot'}`}
              ref={(node) => setMessageRef(message.id, node)}
            >
              <div className="chat-bubble">
                {!isOwnMessage && (
                  <div className="chat-support-sender small fw-bold mb-1">
                    {isAdmin ? (message.sender_name || message.sender_email) : t('support.support')}
                  </div>
                )}
                {message.content && <div>{message.content}</div>}
                {message.file_url && (
                  <div className="mt-2">
                    {message.attachment_type === 'photo' ? (
                      <img
                        src={api.getAuthenticatedMediaUrl(message.file_url)}
                        alt={message.file_name || 'Photo'}
                        style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', cursor: 'pointer' }}
                        onClick={() => window.open(api.getAuthenticatedMediaUrl(message.file_url), '_blank')}
                      />
                    ) : (
                      <a href={api.getAuthenticatedMediaUrl(message.file_url)} target="_blank" rel="noopener noreferrer">
                        {message.file_name || 'Download file'}
                      </a>
                    )}
                  </div>
                )}
                <div className="chat-support-time small mt-1 opacity-75">{createdLabel}</div>
              </div>
            </div>
          );
        }

        return (
          <ListGroup.Item
            key={message.id}
            className={`d-flex ${isOwnMessage ? 'justify-content-end' : 'justify-content-start'} border-0 px-2 py-1`}
            ref={(node) => setMessageRef(message.id, node)}
          >
            <div
              className={`p-2 rounded ${
                isOwnMessage
                  ? 'bg-primary text-white'
                  : 'bg-secondary text-white'
              }`}
              style={{
                maxWidth: '70%',
                backgroundColor: isOwnMessage ? undefined : 'var(--bs-secondary)',
                color: isOwnMessage ? undefined : 'white',
                boxShadow: highlightedMessageId === message.id ? '0 0 0 2px rgba(255, 193, 7, 0.8)' : undefined,
                transition: 'box-shadow 0.25s ease',
              }}
            >
              {!isOwnMessage && (
                <div className="small fw-bold mb-1 d-flex justify-content-between align-items-center gap-2" style={{ color: 'white' }}>
                  <span>{isAdmin ? (message.sender_name || message.sender_email) : t('support.support')}</span>
                  <Button
                    variant="outline-light"
                    size="sm"
                    className="py-0 px-1"
                    title={t('support.replyToMessage')}
                    onClick={() => onReply(message)}
                  >
                    <i className="bi bi-reply me-1" />
                    {t('support.reply')}
                  </Button>
                </div>
              )}
              {message.reply_to && (
                <div
                  className="mb-2 p-2 rounded small"
                  style={{
                    borderLeft: '3px solid rgba(255,255,255,0.5)',
                    backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.2)',
                    color: isOwnMessage ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.9)',
                    cursor: 'pointer',
                  }}
                  onClick={() => onJumpToReply(message.reply_to.id)}
                  title="Перейти к исходному сообщению"
                >
                  <div className="fw-bold" style={{ fontSize: '0.8em' }}>
                    {message.reply_to.sender_name}
                  </div>
                  <div style={{ fontSize: '0.85em', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {(message.reply_to.content || '').slice(0, 200)}
                    {(message.reply_to.content || '').length > 200 ? '…' : ''}
                  </div>
                </div>
              )}
              {message.content && (
                <div className="mb-2" style={{ color: isOwnMessage ? undefined : 'white' }}>
                  <div>{message.content}</div>
                  {!isOwnMessage && isAdmin && message.translated_content && (
                    <div className="mt-1" style={{ fontSize: '0.9em', opacity: 0.9, fontStyle: 'italic' }}>
                      <i className="bi bi-translate me-1" />
                      <span>{message.translated_content}</span>
                    </div>
                  )}
                  {!isOwnMessage && isAdmin && !message.translated_content && message.content && (
                    <div className="mt-2">
                      <Button
                        variant="outline-light"
                        size="sm"
                        onClick={() => onTranslateMessage(message.id)}
                        disabled={translatingMessageId === message.id}
                        style={{ fontSize: '0.75rem' }}
                      >
                        {translatingMessageId === message.id ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-1" />
                            Перевод...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-translate me-1" />
                            Перевести на русский
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  {isAdmin && message.message_type === 'user' && (
                    <div className="mt-2">
                      <Button
                        variant="outline-light"
                        size="sm"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => onMarkUnread(message.id)}
                        disabled={markingUnreadId === message.id}
                      >
                        {markingUnreadId === message.id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <>
                            <i className="bi bi-envelope-open me-1" />
                            {t('support.markAsUnread')}
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {message.file_url && (
                <div className="mb-2">
                  {message.attachment_type === 'photo' ? (
                    <img
                      src={api.getAuthenticatedMediaUrl(message.file_url)}
                      alt={message.file_name || 'Photo'}
                      style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px', cursor: 'pointer' }}
                      onClick={() => window.open(api.getAuthenticatedMediaUrl(message.file_url), '_blank')}
                    />
                  ) : message.attachment_type === 'video' ? (
                    <video
                      src={api.getAuthenticatedMediaUrl(message.file_url)}
                      controls
                      style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px' }}
                    />
                  ) : (
                    <div className="p-3 border rounded" style={{ backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                      <i className="bi bi-file-earmark me-2" />
                      <a
                        href={api.getAuthenticatedMediaUrl(message.file_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: isOwnMessage ? '#fff' : '#0d6efd', textDecoration: 'none' }}
                      >
                        {message.file_name || 'Download file'}
                      </a>
                    </div>
                  )}
                  {message.file_name && (
                    <div className="small mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
                      <i className="bi bi-paperclip me-1" />
                      {message.file_name}
                      {message.file_size && (
                        <span className="ms-1">
                          ({(message.file_size / (1024 * 1024)).toFixed(2)} MB)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="small mt-1 d-flex justify-content-between align-items-center" style={{ color: 'rgba(255,255,255,0.7)' }}>
                <span>{createdLabel}</span>
                {isOwnMessage && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 ms-2 text-decoration-none"
                    style={{ color: 'inherit', opacity: 0.9, fontSize: '0.85rem' }}
                    title={t('support.replyToMessage')}
                    onClick={() => onReply(message)}
                  >
                    <i className="bi bi-reply me-1" />
                    {t('support.reply')}
                  </Button>
                )}
              </div>
            </div>
          </ListGroup.Item>
        );
      })}
      <div ref={messagesEndRef} />
    </ListGroup>
  );
}

export default React.memo(SupportMessageList);
